import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { HUB_CLIENT_BUILD_VERSION } from "@/lib/hubBuildVersion";
import { useIntegratorMe } from "@/lib/me-query";
import {
  fetchQuickBooksAccountingOAuthStatus,
  startQuickBooksAccountingOAuth,
  WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL,
} from "@/lib/quickbooksAccountingOAuth";
import {
  quickBooksAccountingBannerMessage,
  quickBooksAccountingOAuthBannerVisible,
} from "@/lib/quickBooksAccountingMessages";
import { isHubDevInstance } from "@/lib/slowRequestSupportTicket";
import { useConfirmedQueryFailure } from "@/lib/useConfirmedQueryFailure";

function hasQuickBooksAccountingCallbackMessage(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has("quickbooksAccounting") || params.has("quickbooksAccountingError");
}

function isQuickBooksAccountingOperator(email: string | null | undefined): boolean {
  return (email?.trim().toLowerCase() ?? "") === WORKSPACE_AVATAR_SYNC_OPERATOR_EMAIL;
}

export function QuickBooksAccountingOAuthBanner() {
  const location = useLocation();
  const meQuery = useIntegratorMe();
  const operatorSession = isQuickBooksAccountingOperator(meQuery.data?.user.email);
  const queryClient = useQueryClient();
  const callbackMessage = hasQuickBooksAccountingCallbackMessage(location.search);

  useEffect(() => {
    if (!callbackMessage) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ["accounting", "quickbooks-oauth-status"] });
  }, [callbackMessage, queryClient]);

  const statusQuery = useQuery({
    queryKey: ["accounting", "quickbooks-oauth-status"],
    queryFn: fetchQuickBooksAccountingOAuthStatus,
    enabled: operatorSession && meQuery.isSuccess && !callbackMessage,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const connectMutation = useMutation({
    mutationFn: () =>
      startQuickBooksAccountingOAuth({
        returnTo: `${location.pathname}${location.search}${location.hash}`,
        uiOrigin: window.location.origin,
      }),
    onSuccess: (payload) => {
      window.location.assign(payload.authorizeUrl);
    },
  });

  const status = statusQuery.data;
  const statusRequestFailed = useConfirmedQueryFailure(statusQuery);
  const statusUnavailable =
    statusRequestFailed &&
    !isHubDevInstance({
      clientBuildVersion: HUB_CLIENT_BUILD_VERSION,
      viteDev: import.meta.env.DEV === true,
      hostname: typeof window === "undefined" ? "" : window.location.hostname,
    });
  const shouldShow =
    operatorSession &&
    (statusUnavailable ||
      (status?.operatorEligible && status.clientConfigured && quickBooksAccountingOAuthBannerVisible(status)));

  if (!shouldShow) {
    return null;
  }

  const expiringSoon = status?.needsReconnectSoon === true;
  const bannerMessage = quickBooksAccountingBannerMessage(status, statusUnavailable);

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        expiringSoon && !statusUnavailable
          ? "relative z-[40] border-b border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-950"
          : "relative z-[40] border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950"
      }
    >
      <div className="mx-auto flex w-full max-w-[min(100%,2400px)] flex-wrap items-center justify-center gap-x-3 gap-y-2 px-3 sm:px-4 lg:px-6 2xl:px-10">
        <Landmark
          className={
            expiringSoon && !statusUnavailable
              ? "h-4 w-4 shrink-0 text-sky-700"
              : "h-4 w-4 shrink-0 text-amber-700"
          }
          aria-hidden="true"
        />
        <span>{bannerMessage}</span>
        <button
          type="button"
          className={
            expiringSoon && !statusUnavailable
              ? "font-medium text-sky-900 underline decoration-sky-500 underline-offset-2 hover:text-sky-950"
              : "font-medium text-amber-900 underline decoration-amber-500 underline-offset-2 hover:text-amber-950"
          }
          disabled={connectMutation.isPending}
          onClick={() => void connectMutation.mutate()}
        >
          {status?.connected ? "Reconnect QuickBooks Online" : "Connect QuickBooks Online"}
        </button>
        <Link
          to="/accounting/cash-flow"
          className={
            expiringSoon && !statusUnavailable
              ? "font-medium text-sky-900 underline decoration-sky-500 underline-offset-2 hover:text-sky-950"
              : "font-medium text-amber-900 underline decoration-amber-500 underline-offset-2 hover:text-amber-950"
          }
        >
          Open accounting
        </Link>
      </div>
    </div>
  );
}
